import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import auditService from '../../services/auditService';
import { getWarehouses } from '../../services/warehousesService';
import Toast from '../../components/Common/Toast';
import { auditRuleLabel } from '../../utils/auditRuleCatalog';

export default function KardexAuditPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({ warehouse_id: '', status: 'open' });
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getWarehouses({ active_only: true }).then((r) => {
      if (r.success) setWarehouses(r.data || []);
    });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.status) params.status = filters.status;
      const [eventsRes, summaryRes] = await Promise.all([
        auditService.listEvents(params),
        auditService.getSummary(params.warehouse_id ? { warehouse_id: params.warehouse_id } : {}),
      ]);
      if (eventsRes.success) setEvents(eventsRes.data || []);
      if (summaryRes.success) setSummary(summaryRes.data);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar auditoría', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.warehouse_id, filters.status]);

  const resolve = async (id) => {
    try {
      await auditService.resolveEvent(id);
      setToast({ isOpen: true, message: 'Evento marcado como resuelto', type: 'success' });
      load();
    } catch {
      setToast({ isOpen: true, message: 'No se pudo resolver el evento', type: 'error' });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Auditoría de kardex e inventario</h1>
        <p className="text-gray-600 mt-1">
          Alertas operativas por almacén. Solo notifica a usuarios asignados.
        </p>
        <Link
          to="/audit/rules"
          className="inline-flex mt-3 mr-4 text-sm text-teal-700 hover:text-teal-800 hover:underline"
        >
          Configurar reglas →
        </Link>
        <Link
          to="/audit/recipients"
          className="inline-flex mt-3 text-sm text-teal-700 hover:text-teal-800 hover:underline"
        >
          Configurar destinatarios →
        </Link>
      </div>

      {summary && (
        <div className="mb-4 p-4 bg-white border rounded-lg shadow-sm">
          <p className="text-sm text-gray-600">Eventos abiertos: <strong>{summary.open_total ?? 0}</strong></p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <select
          className="border rounded px-3 py-2 text-sm"
          value={filters.warehouse_id}
          onChange={(e) => setFilters((f) => ({ ...f, warehouse_id: e.target.value }))}
        >
          <option value="">Todos los almacenes</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.name || w.code}</option>
          ))}
        </select>
        <select
          className="border rounded px-3 py-2 text-sm"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="open">Abiertos</option>
          <option value="resolved">Resueltos</option>
          <option value="ignored">Ignorados</option>
        </select>
        <button type="button" onClick={load} className="px-4 py-2 bg-teal-600 text-white rounded text-sm">
          Actualizar
        </button>
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-6 text-gray-500">Cargando…</p>
        ) : events.length === 0 ? (
          <p className="p-6 text-gray-500">No hay eventos con los filtros actuales.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3">Regla</th>
                <th className="px-4 py-3">Mensaje</th>
                <th className="px-4 py-3">Almacén</th>
                <th className="px-4 py-3">Detectado</th>
                <th className="px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td className="px-4 py-3">{auditRuleLabel(ev.rule_code, ev.rule_code)}</td>
                  <td className="px-4 py-3">{ev.message}</td>
                  <td className="px-4 py-3">{ev.warehouse?.name || ev.warehouse?.code || '—'}</td>
                  <td className="px-4 py-3">{ev.detected_at ? new Date(ev.detected_at).toLocaleString('es-PE') : '—'}</td>
                  <td className="px-4 py-3 space-x-2">
                    {ev.product_id && ev.warehouse_id && (
                      <Link
                        to={`/reports/kardex/${ev.product_id}/${ev.warehouse_id}`}
                        className="text-teal-600 hover:underline"
                      >
                        Ver kardex
                      </Link>
                    )}
                    {ev.inventory_count_campaign_id && ev.warehouse_id && (
                      <Link
                        to={`/warehouses/${ev.warehouse_id}/inventory-count`}
                        className="text-teal-600 hover:underline"
                      >
                        Toma inventario
                      </Link>
                    )}
                    {ev.status === 'open' && (
                      <button type="button" onClick={() => resolve(ev.id)} className="text-amber-700 hover:underline">
                        Resolver
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
}
